import type { PkType, ProductGroup, ProductMixEntry } from '@/domain';

/**
 * Pure WLU (Workload Unit) calculator.
 *
 * Core idea: WLU per order (minutes) = 60 / UPH_eff
 * where UPH_eff is the harmonic mean of per-product-group UPH weighted by
 * the customer's product mix.
 *
 * Engine M4 derives total_workload_minutes = WLU/đơn × forecast_qty.
 */

const DEFAULT_PK_TYPE: PkType = 'STANDARD';
const DEFAULT_UPH = 60;
const MIN_UPH = 1; // safety floor — avoid div-by-zero

export interface UphLookup {
  /** Resolve UPH for (warehouse_id, customer_id, product_group, pk_type). Engine caller
   *  resolves a Map<key, ConfigValue> before calling — this fn is pure. */
  get(productGroup: ProductGroup, pkType: PkType): number | null;
}

export function uphLookupFromMap(
  map: Map<string, number>,
  warehouseId: string,
  customerId: string,
): UphLookup {
  return {
    get(productGroup, pkType) {
      // customer-specific → warehouse DEFAULT → null
      const specific = `uph:${warehouseId}:${customerId}:${productGroup}:${pkType}`;
      if (map.has(specific)) return map.get(specific)!;
      const def = `uph:${warehouseId}:DEFAULT:${productGroup}:${pkType}`;
      return map.get(def) ?? null;
    },
  };
}

export interface WluInput {
  product_mix: ProductMixEntry[]; // shares sum ≈ 1
  uph: UphLookup;
  pk_type?: PkType; // defaults to STANDARD
}

export interface WluOutput {
  wlu_per_order_minutes: number; // 60 / UPH_eff
  uph_eff: number; // weighted harmonic mean
  uph_breakdown: { product_group: ProductGroup; uph: number; share: number }[];
  used_fallback: boolean; // true if any product group missed
}

/**
 * UPH_eff via harmonic-mean weighting (because we sum times, not throughputs).
 *
 *   time_per_order_avg = Σ share_i * (60 / UPH_i)
 *   UPH_eff = 60 / time_per_order_avg
 */
export function computeWlu(input: WluInput): WluOutput {
  const pk = input.pk_type ?? DEFAULT_PK_TYPE;

  // Normalise shares (defensive — caller should have already)
  const totalShare = input.product_mix.reduce((s, m) => s + m.share, 0);
  const normalised = totalShare > 0 ? input.product_mix : [];

  let weightedMinutesPerOrder = 0;
  let fallbackUsed = false;
  const breakdown: WluOutput['uph_breakdown'] = [];

  for (const entry of normalised) {
    const share = entry.share / totalShare;
    let uph = input.uph.get(entry.product_group, pk);
    if (uph == null || uph <= 0) {
      uph = DEFAULT_UPH;
      fallbackUsed = true;
    }
    breakdown.push({ product_group: entry.product_group, uph, share });
    weightedMinutesPerOrder += share * (60 / Math.max(MIN_UPH, uph));
  }

  if (normalised.length === 0) {
    return {
      wlu_per_order_minutes: 60 / DEFAULT_UPH,
      uph_eff: DEFAULT_UPH,
      uph_breakdown: [],
      used_fallback: true,
    };
  }

  const uphEff = 60 / Math.max(0.001, weightedMinutesPerOrder);
  return {
    wlu_per_order_minutes: weightedMinutesPerOrder,
    uph_eff: uphEff,
    uph_breakdown: breakdown,
    used_fallback: fallbackUsed,
  };
}

export interface WorkloadInput {
  wlu_per_order_minutes: number;
  forecast_qty: number;
}

export interface WorkloadOutput {
  total_workload_minutes: number;
  total_workload_hours: number;
}

export function computeTotalWorkload(input: WorkloadInput): WorkloadOutput {
  const minutes = Math.max(0, input.wlu_per_order_minutes * input.forecast_qty);
  return {
    total_workload_minutes: minutes,
    total_workload_hours: minutes / 60,
  };
}

/** Convenience: minutes → headcount @ 8h shift × 480 min. Defensive against /0. */
export function minutesToHeadcount(minutes: number, shiftMinutes = 480): number {
  if (shiftMinutes <= 0) return 0;
  return minutes / shiftMinutes;
}
